import React, { createContext, useContext, useState, useEffect } from 'react';
import { Lumisial } from '../lib/supabase';
import { useAuth } from './AuthContext';
import {
  leerLumisialPersistido,
  guardarLumisialPersistido,
  olvidarLumisialPersistido,
} from '../utils/lumisialPersistido';

interface LumisialContextType {
  selectedLumisial: Lumisial | null;
  setSelectedLumisial: (lumisial: Lumisial | null) => void;
}

const LumisialContext = createContext<LumisialContextType | undefined>(undefined);

export const LumisialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { usuario } = useAuth();
  const [selectedLumisial, setSelectedLumisial] = useState<Lumisial | null>(null);

  // Rehidratar solo cuando se conoce al usuario, y descartar lo guardado por otro.
  const [lastUserId, setLastUserId] = useState<string | null | undefined>(undefined);
  const currentUserId = usuario?.user_id ?? null;
  if (currentUserId !== lastUserId) {
    setLastUserId(currentUserId);
    setSelectedLumisial(leerLumisialPersistido(currentUserId));
  }

  useEffect(() => {
    // Sin usuario conocido no se toca lo persistido: durante el arranque el estado
    // todavía es null y borrarlo ahí eliminaría la selección legítima en cada recarga.
    if (!currentUserId) return;
    if (selectedLumisial) {
      guardarLumisialPersistido(currentUserId, selectedLumisial);
    } else {
      olvidarLumisialPersistido();
    }
  }, [selectedLumisial, currentUserId]);

  return (
    <LumisialContext.Provider value={{ selectedLumisial, setSelectedLumisial }}>
      {children}
    </LumisialContext.Provider>
  );
};

export const useLumisial = () => {
  const context = useContext(LumisialContext);
  if (context === undefined) {
    throw new Error('useLumisial must be used within a LumisialProvider');
  }
  return context;
};
